import * as React from 'react';
import * as styles from './styles.scss';
import { useInnerFocusTracker } from 'owa-react-hooks/lib/useInnerFocusTracker';
import type { BucketPayload } from 'owa-timestream-asset-sdk-bucket';
import { bucketTitlePlaceholder } from '../bucketStrings.locstring.json';
import { CharmControlColorPattern, CharmPickerBackground } from 'owa-charmcontrol';
import type { ITextFieldStyles } from '@fluentui/react/lib/TextField';
import { observer } from 'mobx-react-lite';
import { TextFieldWithHiddenLabel } from 'owa-timestream-asset-sdk';
import CharmPicker from 'owa-charmpicker/lib/components/CharmPicker';
import loc from 'owa-localize';
import useBucketViewCharmPickerState from '../hooks/useBucketViewCharmPickerState';
import useBucketViewTitleState from '../hooks/useBucketViewTitleState';
import { getIsDarkTheme } from 'owa-fabric-theme';

import classNames from 'classnames';

const textFieldStyles: Partial<ITextFieldStyles> = {
    root: styles.bucketTitle,
    fieldGroup: styles.bucketTitleFieldGroup,
};

interface BucketViewTitleProps extends BucketPayload {
    color?: string;
}

export default observer(function BucketViewTitle({ name, charmId, color }: BucketViewTitleProps) {
    const titleAndCharmWrapperRef = React.useRef<HTMLDivElement>(null);
    const { hasFocusState, onFocusCapture, onBlurCapture } = useInnerFocusTracker(
        titleAndCharmWrapperRef
    );
    const textFieldProps = useBucketViewTitleState(name);
    const { isPickingCharm, charmPickerProps } = useBucketViewCharmPickerState(charmId, color);

    const customFieldStyles: Partial<ITextFieldStyles> = {
        field: hasFocusState && !isPickingCharm ? styles.bucketTitleField : styles.bucketTitleInput,
    };

    return (
        <div
            ref={titleAndCharmWrapperRef}
            onFocusCapture={onFocusCapture}
            onBlurCapture={onBlurCapture}
            className={classNames(styles.titleAndCharmWrapper, {
                [styles.emptyTitle]: !name,
            })}
            tabIndex={-1 /* Prevent Safari from putting focus on the body */}>
            {(!!charmId || hasFocusState || name == '' || isPickingCharm) && (
                <CharmPicker
                    {...charmPickerProps}
                    charmClassName={styles.charmIconTitle}
                    buttonClassName={getIsDarkTheme() ? styles.charmButtonDark : styles.charmButton}
                    resultContainerClassName={styles.charmResultContainer}
                    colorPattern={CharmControlColorPattern.Normal}
                    background={
                        CharmPickerBackground.CalendarColor /* avoid CharmPicker auto balancing charm colors which looks bad on colored assets in dark mode */
                    }
                    disabled={false}
                />
            )}
            <TextFieldWithHiddenLabel
                {...textFieldProps}
                placeholder={loc(bucketTitlePlaceholder)}
                borderless={true}
                multiline={true}
                autoAdjustHeight={true}
                resizable={false}
                styles={{ ...textFieldStyles, ...customFieldStyles }}
            />
        </div>
    );
});
